<script setup lang="ts">
  import { Typo, Icon } from '@travelata-libs/ui-kit';
  import { computed, onMounted, ref } from 'vue';
  import {
    getToursUrl,
    getHotToursUrl,
    getHotelOffersUrl,
    getHistorySearch,
    getHotelUrl,
    getSingleHotelId,
    useSearchCriteria,
    type SearchCriteria,
    type SearchCriteriaHistory,
    type SearchCriteriaModel,
  } from '@/entities/searchCriteria';
  import type { SearchFormGoal, SearchFormTracking } from '@/entities/searchForm';
  import type { AccountUser } from '@/features/account';
  import { LeadBanner } from '@/features/leadBanner';
  import { OfficeTourButton } from '@/features/officeTour';
  import { HistorySearchCard } from '@/features/searchHistory';
  import { SwitchCityField, type SwitchCity } from '@/features/switchCity';
  import { useTravelataAppConfig } from '@/shared/composables';
  import { STAY_CALENDAR_AB_TEST } from '@/shared/config/abTests';
  import { CountryResortsUrl, FormDataUrl, IntelligentSearchUrl } from '@/shared/config/formData';
  import { LocalStorageKey } from '@/shared/config/localStorage';
  import { PagesUrl, ProductType, SEARCH_SOURCE_BY_PAGE, type PageContent } from '@/shared/config/pages';
  import { abTests } from '@/shared/lib/AbTests';
  import SkolkovoLogo from '@/shared/ui/logos/SkolkovoLogo.vue';
  import TravelataLogo from '@/shared/ui/logos/TravelataLogo.vue';
  import { SearchForm } from '@/widgets/searchForm';
  import { INDEX_MORE_NAV_LINKS, INDEX_PRIMARY_NAV_LINKS, SiteHeader } from '@/widgets/siteHeader';
  import IndexBackground from './IndexBackground.vue';
  import tabs, { PRODUCT_TYPE_TABS } from '../config/tabs';
  import { type TabType } from '../model/types';
  import IndexPageMobile from './mobile/IndexPageMobile.vue';

  interface IndexProps {
    cities?: SwitchCity[];
    currentCity?: SwitchCity | null;
    user?: AccountUser | null;
    accountUrl?: string;
    isVisibleRoomButton?: boolean;
    mobile?: boolean;
    pageContent?: PageContent;
    productType?: ProductType;
  }

  const props = withDefaults(defineProps<IndexProps>(), {
    isVisibleRoomButton: true,
    mobile: false,
    pageContent: undefined,
    productType: undefined,
  });

  interface IndexEmits {
    (e: 'login'): void;
    (e: 'logout'): void;
    (e: 'change:city', data: SwitchCity): void;
    (e: 'buy'): void;
    (e: 'chat'): void;
    (e: 'search', criteria: SearchCriteria): void;
    (e: 'track', goal: SearchFormGoal): void;
  }
  const emit = defineEmits<IndexEmits>();
  const TOURS_TRACKING: SearchFormTracking = { pageType: 'main', productType: 'tour' };
  const HOTELS_TRACKING: SearchFormTracking = { pageType: 'main', productType: 'hotel' };

  const appConfig = useTravelataAppConfig();
  const productTab = computed(() => (props.productType ? PRODUCT_TYPE_TABS[props.productType] : null));
  const activeTab = ref<TabType>(productTab.value ?? 'tours');
  const historySearch = ref<SearchCriteriaHistory[]>([]);

  const onMobileBack = (): void => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const withUrlCriteria = !props.productType;

  const isToursOpen = computed(() => activeTab.value === 'tours');
  const isHotOpen = computed(() => activeTab.value === 'tours-hot');
  const isHotelsOpen = computed(() => activeTab.value === 'hotels');
  const isStayCalendar = !props.mobile && abTests.hasTest(STAY_CALENDAR_AB_TEST);
  const isRailwayOpen = computed(() => activeTab.value === 'railway');

  const toursModel = useSearchCriteria({
    withPrices: true,
    withHistory: true,
    lastCriteriaKey: LocalStorageKey.TourCriteria,
    isOpen: isToursOpen,
    withUrlCriteria,
    formUrl: FormDataUrl.Tours,
    searchUrl: IntelligentSearchUrl.Tours,
    resortsUrl: CountryResortsUrl.Tours,
  });
  const hotModel = useSearchCriteria({
    lastCriteriaKey: LocalStorageKey.TourCriteriaTury,
    isOpen: isHotOpen,
    withUrlCriteria,
    formUrl: FormDataUrl.Hot,
    searchUrl: IntelligentSearchUrl.Hot,
    resortsUrl: CountryResortsUrl.Hot,
  });
  const hotelsModel = useSearchCriteria({
    lastCriteriaKey: LocalStorageKey.TourCriteriaHotelOffers,
    isOpen: isHotelsOpen,
    withUrlCriteria,
    isStayCalendar,
    formUrl: FormDataUrl.Hotels,
    searchUrl: IntelligentSearchUrl.Hotels,
    resortsUrl: CountryResortsUrl.Hotels,
  });
  const railwayModel = useSearchCriteria({
    lastCriteriaKey: LocalStorageKey.TourCriteriaRailwayTours,
    isOpen: isRailwayOpen,
    withUrlCriteria,
    formUrl: FormDataUrl.Railway,
    searchUrl: IntelligentSearchUrl.Railway,
    resortsUrl: CountryResortsUrl.Railway,
  });

  const redirect = (path: string, query: string): void => {
    window.location.href = `${appConfig.baseHost}/${path}#?${query}`;
  };

  interface SearchTarget {
    tab: TabType;
    serpUrl: PagesUrl;
    getSerpQuery: (searchCriteria: SearchCriteria) => string;
    productType?: ProductType;
  }

  const createSearchHandler = (
    criteriaModel: SearchCriteriaModel,
    { tab, serpUrl, getSerpQuery, productType }: SearchTarget,
  ) => {
    return (searchCriteria: SearchCriteria): void => {
      if (tab === productTab.value) {
        emit('search', searchCriteria);
        return;
      }

      criteriaModel.saveSearch(searchCriteria);

      const hotelId = getSingleHotelId(searchCriteria.destination);
      if (hotelId) {
        const hotelQuery = getHotelUrl(searchCriteria, {
          productType,
          searchSource: SEARCH_SOURCE_BY_PAGE[serpUrl],
        });
        redirect(`${PagesUrl.GotoHotel}/${hotelId}`, hotelQuery);
        return;
      }

      redirect(serpUrl, getSerpQuery(searchCriteria));
    };
  };

  const onToursSearch = createSearchHandler(toursModel, {
    tab: 'tours',
    serpUrl: PagesUrl.SerpTours,
    getSerpQuery: getToursUrl,
  });
  const onHotSearch = createSearchHandler(hotModel, {
    tab: 'tours-hot',
    serpUrl: PagesUrl.SerpHot,
    getSerpQuery: getHotToursUrl,
  });
  const onHotelsSearch = createSearchHandler(hotelsModel, {
    tab: 'hotels',
    serpUrl: PagesUrl.SerpHotels,
    getSerpQuery: getHotelOffersUrl,
    productType: ProductType.HotelOffer,
  });
  const onRailwaySearch = createSearchHandler(railwayModel, {
    tab: 'railway',
    serpUrl: PagesUrl.SerpRailway,
    getSerpQuery: getToursUrl,
    productType: ProductType.RailwayTour,
  });

  const currentTab = computed(() => tabs.find(({ type }) => type === activeTab.value));
  const mobileTabs = computed(() => tabs.filter(({ type }) => type !== 'railway' || type === productTab.value));

  const activeForm = computed(() => {
    switch (activeTab.value) {
      case 'hotels': {
        return {
          model: hotelsModel,
          onSearch: onHotelsSearch,
          isVisibleDeparture: false,
          isVisibleRoomButton: false,
          prices: undefined,
          tracking: HOTELS_TRACKING,
        };
      }
      case 'tours-hot': {
        return {
          model: hotModel,
          onSearch: onHotSearch,
          isVisibleDeparture: true,
          isVisibleRoomButton: props.isVisibleRoomButton,
          prices: undefined,
          tracking: undefined,
        };
      }
      case 'railway': {
        return {
          model: railwayModel,
          onSearch: onRailwaySearch,
          isVisibleDeparture: true,
          isVisibleRoomButton: false,
          prices: undefined,
          tracking: undefined,
        };
      }
      default: {
        return {
          model: toursModel,
          onSearch: onToursSearch,
          isVisibleDeparture: true,
          isVisibleRoomButton: props.isVisibleRoomButton,
          prices: toursModel.prices.value,
          tracking: TOURS_TRACKING,
        };
      }
    }
  });

  const activeCriteria = computed({
    get: () => activeForm.value.model.model.value,
    set: (value) => {
      activeForm.value.model.model.value = value;
    },
  });

  const onClickTab = (type: TabType) => {
    activeTab.value = type;
  };

  onMounted(async () => {
    const historySearchData = await getHistorySearch(true);
    historySearch.value = historySearchData;
  });
</script>

<template>
  <IndexPageMobile
    v-if="mobile && currentTab"
    :active-tab="activeTab"
    :tabs="mobileTabs"
    :model-value="activeCriteria"
    :is-visible-departure="activeForm.isVisibleDeparture"
    :is-visible-room-button="activeForm.isVisibleRoomButton"
    :prices="activeForm.prices"
    :form-url="activeForm.model.formUrl"
    :search-url="activeForm.model.searchUrl"
    :resorts-url="activeForm.model.resortsUrl"
    :account-url="accountUrl"
    :history-search="historySearch"
    :cities="cities"
    :current-city="currentCity"
    :page-content="pageContent"
    :lead-title="currentTab.leadTitle"
    :lead-subtitle="currentTab.leadSubtitle"
    :modal-title="currentTab.modalTitle"
    :modal-subtitle="currentTab.modalSubtitle"
    :search-btn-text="currentTab.searchBtnText"
    :date-field-label="currentTab.dateFieldLabel"
    :nights-field-label="currentTab.nightsFieldLabel"
    :tracking="activeForm.tracking"
    @update:modelValue="activeCriteria = $event"
    @select="onClickTab"
    @search="activeForm.onSearch"
    @back="onMobileBack"
    @chat="emit('chat')"
    @change:city="emit('change:city', $event)"
    @track="emit('track', $event)"
  />

  <IndexBackground
    v-else-if="currentTab"
    :tab="activeTab"
    class="index-page"
  >
    <div>
      <SiteHeader
        :user="user"
        :visible-logos="false"
        :account-url="accountUrl"
        :should-adaptive="false"
        :button-bordered="false"
        :primary="INDEX_PRIMARY_NAV_LINKS"
        :more="INDEX_MORE_NAV_LINKS"
        class="index-page__header"
        @login="emit('login')"
        @logout="emit('logout')"
      >
        <template #action>
          <SwitchCityField
            :current-city="currentCity"
            :cities="cities"
            :redirect="false"
            class="index-page__switch-city"
            @change="emit('change:city', $event)"
          />
        </template>
      </SiteHeader>
    </div>
    <div class="index-page__container">
      <div class="index-page__main">
        <div class="index-page__logos">
          <TravelataLogo class="index-page__travelata-logo" />
          <div class="index-page__up-right">
            <SkolkovoLogo class="index-page__skolkovo-logo" />
            <OfficeTourButton
              size="l"
              class="index-page__office-tour"
              @buy="emit('buy')"
            />
          </div>
        </div>
      </div>

      <div class="index-page__text-container">
        <Typo
          class="index-page__text"
          variant="h-1"
        >
          {{ currentTab.title }}
          <br />
          или поручите подбор нашему эксперту
        </Typo>
      </div>
      <div class="index-page__tabs">
        <div
          v-for="tab in tabs"
          :key="tab.type"
          :class="[
            'index-page__tab',
            {
              'index-page__tab_active': activeTab === tab.type,
            },
          ]"
          @click="onClickTab(tab.type)"
        >
          <div class="index-page__tab-circle">
            <Icon
              size="xl"
              class="index-page__tab-icon"
              :name="tab.icon"
            />
            <div
              v-if="tab.type === 'tours-hot'"
              class="index-page__tab-discount"
            >
              <Typo
                variant="body-m-700-short"
                class="index-page__tab-discount-typo"
              >
                -50%
              </Typo>
            </div>
          </div>
          <Typo
            class="index-page__tab-typo"
            variant="body-l-400"
          >
            {{ tab.text }}
          </Typo>
        </div>
      </div>
      <SearchForm
        v-if="isToursOpen"
        :key="`${isToursOpen}_tours`"
        v-model="toursModel.model.value"
        :is-visible-room-button="isVisibleRoomButton"
        :prices="toursModel.prices.value"
        :form-url="toursModel.formUrl"
        :search-url="toursModel.searchUrl"
        :resorts-url="toursModel.resortsUrl"
        :search-btn-text="currentTab.searchBtnText"
        :date-field-label="currentTab.dateFieldLabel"
        size="l"
        class="index-page__search-form"
        :tracking="TOURS_TRACKING"
        @search="onToursSearch"
        @track="emit('track', $event)"
      />
      <SearchForm
        v-if="isHotOpen"
        :key="`${isHotOpen}_hot`"
        v-model="hotModel.model.value"
        :is-visible-room-button="isVisibleRoomButton"
        :form-url="hotModel.formUrl"
        :search-url="hotModel.searchUrl"
        :resorts-url="hotModel.resortsUrl"
        :search-btn-text="currentTab.searchBtnText"
        :date-field-label="currentTab.dateFieldLabel"
        size="l"
        class="index-page__search-form"
        @search="onHotSearch"
      />
      <SearchForm
        v-if="isHotelsOpen"
        :key="`${isHotelsOpen}_hotels`"
        v-model="hotelsModel.model.value"
        :is-visible-departure="false"
        :form-url="hotelsModel.formUrl"
        :search-url="hotelsModel.searchUrl"
        :resorts-url="hotelsModel.resortsUrl"
        :is-visible-room-button="false"
        :search-btn-text="currentTab.searchBtnText"
        :date-field-label="currentTab.dateFieldLabel"
        :tourists-field-label="currentTab.touristsFieldLabel"
        :nights-summary-prefix="currentTab.nightsSummaryPrefix"
        :is-stay-calendar="isStayCalendar"
        size="l"
        class="index-page__search-form"
        :tracking="HOTELS_TRACKING"
        @search="onHotelsSearch"
        @track="emit('track', $event)"
      />
      <SearchForm
        v-if="isRailwayOpen"
        :key="`${isRailwayOpen}_railway`"
        v-model="railwayModel.model.value"
        :form-url="railwayModel.formUrl"
        :search-url="railwayModel.searchUrl"
        :resorts-url="railwayModel.resortsUrl"
        :is-visible-room-button="false"
        :search-btn-text="currentTab.searchBtnText"
        :date-field-label="currentTab.dateFieldLabel"
        size="l"
        class="index-page__search-form"
        @search="onRailwaySearch"
      />

      <div class="index-page__lead-banner-container">
        <LeadBanner
          :title="currentTab.leadTitle"
          :subtitle="currentTab.leadSubtitle"
          :modal-title="currentTab.modalTitle"
          class="index-page__lead-banner"
        />
      </div>

      <div
        v-if="historySearch.length"
        class="index-page__history"
      >
        <Typo
          class="index-page__history-title"
          variant="body-l-500"
        >
          Вы искали в прошлый раз
        </Typo>
        <div class="index-page__history-container">
          <HistorySearchCard
            v-for="(value, index) in historySearch"
            :key="index"
            :history-search="value"
            :is-first="index === 0"
            :is-last="index === historySearch.length - 1"
          />
        </div>
      </div>
    </div>
  </IndexBackground>
</template>

<style lang="scss" scoped>
  .index-page {
    $root: &;

    @keyframes tab-scale {
      0% {
        transform: scale(1);
      }

      35% {
        transform: scale(0.97);
      }

      70% {
        transform: scale(1.02);
      }

      100% {
        transform: scale(1);
      }
    }

    &__search-form {
      background: transparent;
      margin-bottom: var(--spacing-xxl);
      padding-bottom: 0;
    }

    &__office-tour {
      height: 48px;
    }

    &__switch-city {
      margin-right: var(--spacing-xxs);
    }

    &__up-right {
      align-items: center;
      display: flex;
      gap: var(--spacing-xxl);
    }

    &__container {
      margin: 0 auto;
      max-width: $max-width-xl;
    }

    &__text-container {
      margin: 0 auto;
      margin-bottom: var(--spacing-xxl);
      max-width: 786px;
    }

    &__text {
      color: var(--color-text-inverse);
      text-align: center;
    }

    &__lead-banner-container {
      margin-left: auto;
      margin-right: auto;
    }

    &__lead-banner {
      margin: auto;
      max-width: 867px;
    }

    &__skolkovo-logo {
      height: 36px;
      width: 122px;
    }

    &__travelata-logo {
      height: 48px;
      width: 205px;
    }

    &__tab-circle {
      align-items: center;
      background: rgb(255 255 255 / 0.33);
      border-radius: 100%;
      display: flex;
      height: 60px;
      justify-content: center;
      width: 60px;
    }

    &__tab-icon {
      fill: var(--color-text-inverse);
    }

    &__tab {
      align-items: center;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
      position: relative;
      transition: transform 0.2s cubic-bezier(0.33, 1, 0.68, 1);

      &:not(#{$root}__tab_active):hover {
        transform: scale(1.04);
      }

      &_active {
        animation: tab-scale 0.35s cubic-bezier(0.33, 1, 0.68, 1);
        #{$root}__tab-circle {
          background-color: var(--color-text-inverse);
        }
        #{$root}__tab-icon {
          fill: var(--color-text-primary);
        }
      }
    }

    &__tab-discount-typo {
      line-height: normal;
    }

    &__tab-discount {
      align-items: center;
      background-color: var(--color-red-50);
      border-radius: var(--rounding-l);
      color: var(--color-text-inverse);
      display: flex;
      height: 20px;
      justify-content: center;
      left: -5px;
      position: absolute;
      top: -11px;
      transform: rotate(-8deg);
      width: 42px;
    }

    &__history-title {
      color: var(--color-text-inverse);
    }

    &__tabs {
      align-items: center;
      display: flex;
      gap: var(--spacing-xl);
      justify-content: center;
      margin: 0 auto;
    }

    &__tab-typo {
      color: var(--color-text-inverse);
    }

    &__header {
      background: var(--color-text-primary);
      height: 48px;
      padding-bottom: var(--spacing-s);
      position: relative;
      z-index: 5;
    }

    &__logos {
      display: flex;
      height: 48px;
      justify-content: space-between;
      margin: var(--spacing-xl) auto;
      width: 100%;
    }

    &__history {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
      margin: 0 auto;
      margin-top: var(--spacing-xxxl);
      max-width: $max-width-xl;
    }

    &__history-container {
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-s);
    }

    @include responsive-max-width('&__lead-banner-container');
    @include responsive-max-width('&__logos');
    @include responsive-max-width('&__history');
  }
</style>
